import { type Command, type Event, parseCommand } from '@budovan/bridge';

/* The bridge transport: the app's WebView (React Native or Flutter) or a parent page embedding the
 * viewer. Messages in both directions follow @budovan/bridge; anything else is ignored. */

interface NativeHost {
  ReactNativeWebView?: { postMessage(message: string): void };
  flutter_inappwebview?: { callHandler(name: string, ...args: unknown[]): unknown };
}

export interface Bridge {
  send(event: Event): void;
}

export function createBridge(onCommand: (command: Command) => void): Bridge {
  const host = window as unknown as NativeHost;
  const receive = (event: MessageEvent): void => {
    // In a frame only the page that frames the viewer may command it, not another frame or window.
    const native = Boolean(host.ReactNativeWebView ?? host.flutter_inappwebview);
    if (!native && event.source !== window.parent) return;
    const command = parseCommand(event.data);
    if (command) onCommand(command);
  };
  window.addEventListener('message', receive);
  // React Native posts to document on Android.
  document.addEventListener('message', receive as EventListener);
  return {
    send(event: Event): void {
      const text = JSON.stringify(event);
      const reactNative = host.ReactNativeWebView;
      // React Native's postMessage takes no target origin: the WebView delivers only to its own app.
      // (Called through Reflect because the lint rule for window.postMessage cannot tell the two apart.)
      if (reactNative) Reflect.apply(reactNative.postMessage, reactNative, [text]);
      else if (host.flutter_inappwebview) void host.flutter_inappwebview.callHandler('budovanTwin', text);
      else if (window.parent !== window) window.parent.postMessage(text, '*');
    },
  };
}
